import { isRegistrar } from './registrars.js';

// A company's billing details (#41 part 5): who its Stripe customers bill, on the subscription row's `billing.customer` and
// the plAutoPayment row's `billing` block (and `pendingChange.proposed.billing` while signatories sign it). They are
// prefilled from the company (the Companies House registered office, `profileInfo.contactEmail` and `contactPhone`) and
// never written back to it. The lambdas normalise and check them the same way (ns-lambda-ts), with the same country list
// and wording, so the card can show a problem beside its field before the server refuses it.

export interface BillingAddress {
  address1: string;
  address2: string | null;
  city: string;
  postcode: string;
  // ISO 3166-1 alpha-2, as Stripe takes it
  country: string;
}

export interface BillingDetails {
  email: string;
  phone: string | null;
  address: BillingAddress;
}

// What the form holds as typed: one string per field ('' for nothing)
export interface BillingDraft {
  email: string;
  phone: string;
  address1: string;
  address2: string;
  city: string;
  postcode: string;
  country: string;
}

export type BillingField = keyof BillingDraft;

export interface BillingProblem {
  field: BillingField;
  reason: string;
}

export const EMPTY_BILLING_DRAFT: BillingDraft = {
  email: '',
  phone: '',
  address1: '',
  address2: '',
  city: '',
  postcode: '',
  country: '',
};

export const MAX_EMAIL_LENGTH = 254;
// Stripe's own limit on a customer's phone: a longer one would pass here only for Stripe to refuse it, which for the
// automatic payments customer is after the signatories have signed it
export const MAX_PHONE_LENGTH = 20;
export const MAX_FIELD_LENGTH = 200;

// ISO 3166-1 alpha-2 codes and their names (en-GB), the countries a billing address can be in
export const COUNTRY_NAMES: Readonly<Record<string, string>> = {
  AD: 'Andorra',
  AE: 'United Arab Emirates',
  AF: 'Afghanistan',
  AG: 'Antigua & Barbuda',
  AI: 'Anguilla',
  AL: 'Albania',
  AM: 'Armenia',
  AO: 'Angola',
  AQ: 'Antarctica',
  AR: 'Argentina',
  AS: 'American Samoa',
  AT: 'Austria',
  AU: 'Australia',
  AW: 'Aruba',
  AX: 'Åland Islands',
  AZ: 'Azerbaijan',
  BA: 'Bosnia & Herzegovina',
  BB: 'Barbados',
  BD: 'Bangladesh',
  BE: 'Belgium',
  BF: 'Burkina Faso',
  BG: 'Bulgaria',
  BH: 'Bahrain',
  BI: 'Burundi',
  BJ: 'Benin',
  BL: 'St Barthélemy',
  BM: 'Bermuda',
  BN: 'Brunei',
  BO: 'Bolivia',
  BQ: 'Caribbean Netherlands',
  BR: 'Brazil',
  BS: 'Bahamas',
  BT: 'Bhutan',
  BV: 'Bouvet Island',
  BW: 'Botswana',
  BY: 'Belarus',
  BZ: 'Belize',
  CA: 'Canada',
  CC: 'Cocos (Keeling) Islands',
  CD: 'Congo - Kinshasa',
  CF: 'Central African Republic',
  CG: 'Congo - Brazzaville',
  CH: 'Switzerland',
  CI: 'Côte d’Ivoire',
  CK: 'Cook Islands',
  CL: 'Chile',
  CM: 'Cameroon',
  CN: 'China',
  CO: 'Colombia',
  CR: 'Costa Rica',
  CU: 'Cuba',
  CV: 'Cape Verde',
  CW: 'Curaçao',
  CX: 'Christmas Island',
  CY: 'Cyprus',
  CZ: 'Czechia',
  DE: 'Germany',
  DJ: 'Djibouti',
  DK: 'Denmark',
  DM: 'Dominica',
  DO: 'Dominican Republic',
  DZ: 'Algeria',
  EC: 'Ecuador',
  EE: 'Estonia',
  EG: 'Egypt',
  EH: 'Western Sahara',
  ER: 'Eritrea',
  ES: 'Spain',
  ET: 'Ethiopia',
  FI: 'Finland',
  FJ: 'Fiji',
  FK: 'Falkland Islands',
  FM: 'Micronesia',
  FO: 'Faroe Islands',
  FR: 'France',
  GA: 'Gabon',
  GB: 'United Kingdom',
  GD: 'Grenada',
  GE: 'Georgia',
  GF: 'French Guiana',
  GG: 'Guernsey',
  GH: 'Ghana',
  GI: 'Gibraltar',
  GL: 'Greenland',
  GM: 'Gambia',
  GN: 'Guinea',
  GP: 'Guadeloupe',
  GQ: 'Equatorial Guinea',
  GR: 'Greece',
  GS: 'South Georgia & South Sandwich Islands',
  GT: 'Guatemala',
  GU: 'Guam',
  GW: 'Guinea-Bissau',
  GY: 'Guyana',
  HK: 'Hong Kong SAR China',
  HM: 'Heard & McDonald Islands',
  HN: 'Honduras',
  HR: 'Croatia',
  HT: 'Haiti',
  HU: 'Hungary',
  ID: 'Indonesia',
  IE: 'Ireland',
  IL: 'Israel',
  IM: 'Isle of Man',
  IN: 'India',
  IO: 'British Indian Ocean Territory',
  IQ: 'Iraq',
  IR: 'Iran',
  IS: 'Iceland',
  IT: 'Italy',
  JE: 'Jersey',
  JM: 'Jamaica',
  JO: 'Jordan',
  JP: 'Japan',
  KE: 'Kenya',
  KG: 'Kyrgyzstan',
  KH: 'Cambodia',
  KI: 'Kiribati',
  KM: 'Comoros',
  KN: 'St Kitts & Nevis',
  KP: 'North Korea',
  KR: 'South Korea',
  KW: 'Kuwait',
  KY: 'Cayman Islands',
  KZ: 'Kazakhstan',
  LA: 'Laos',
  LB: 'Lebanon',
  LC: 'St Lucia',
  LI: 'Liechtenstein',
  LK: 'Sri Lanka',
  LR: 'Liberia',
  LS: 'Lesotho',
  LT: 'Lithuania',
  LU: 'Luxembourg',
  LV: 'Latvia',
  LY: 'Libya',
  MA: 'Morocco',
  MC: 'Monaco',
  MD: 'Moldova',
  ME: 'Montenegro',
  MF: 'St Martin',
  MG: 'Madagascar',
  MH: 'Marshall Islands',
  MK: 'North Macedonia',
  ML: 'Mali',
  MM: 'Myanmar (Burma)',
  MN: 'Mongolia',
  MO: 'Macao SAR China',
  MP: 'Northern Mariana Islands',
  MQ: 'Martinique',
  MR: 'Mauritania',
  MS: 'Montserrat',
  MT: 'Malta',
  MU: 'Mauritius',
  MV: 'Maldives',
  MW: 'Malawi',
  MX: 'Mexico',
  MY: 'Malaysia',
  MZ: 'Mozambique',
  NA: 'Namibia',
  NC: 'New Caledonia',
  NE: 'Niger',
  NF: 'Norfolk Island',
  NG: 'Nigeria',
  NI: 'Nicaragua',
  NL: 'Netherlands',
  NO: 'Norway',
  NP: 'Nepal',
  NR: 'Nauru',
  NU: 'Niue',
  NZ: 'New Zealand',
  OM: 'Oman',
  PA: 'Panama',
  PE: 'Peru',
  PF: 'French Polynesia',
  PG: 'Papua New Guinea',
  PH: 'Philippines',
  PK: 'Pakistan',
  PL: 'Poland',
  PM: 'St Pierre & Miquelon',
  PN: 'Pitcairn Islands',
  PR: 'Puerto Rico',
  PS: 'Palestinian Territories',
  PT: 'Portugal',
  PW: 'Palau',
  PY: 'Paraguay',
  QA: 'Qatar',
  RE: 'Réunion',
  RO: 'Romania',
  RS: 'Serbia',
  RU: 'Russia',
  RW: 'Rwanda',
  SA: 'Saudi Arabia',
  SB: 'Solomon Islands',
  SC: 'Seychelles',
  SD: 'Sudan',
  SE: 'Sweden',
  SG: 'Singapore',
  SH: 'St Helena',
  SI: 'Slovenia',
  SJ: 'Svalbard & Jan Mayen',
  SK: 'Slovakia',
  SL: 'Sierra Leone',
  SM: 'San Marino',
  SN: 'Senegal',
  SO: 'Somalia',
  SR: 'Suriname',
  SS: 'South Sudan',
  ST: 'São Tomé & Príncipe',
  SV: 'El Salvador',
  SX: 'Sint Maarten',
  SY: 'Syria',
  SZ: 'Eswatini',
  TC: 'Turks & Caicos Islands',
  TD: 'Chad',
  TF: 'French Southern Territories',
  TG: 'Togo',
  TH: 'Thailand',
  TJ: 'Tajikistan',
  TK: 'Tokelau',
  TL: 'Timor-Leste',
  TM: 'Turkmenistan',
  TN: 'Tunisia',
  TO: 'Tonga',
  TR: 'Türkiye',
  TT: 'Trinidad & Tobago',
  TV: 'Tuvalu',
  TW: 'Taiwan',
  TZ: 'Tanzania',
  UA: 'Ukraine',
  UG: 'Uganda',
  UM: 'US Outlying Islands',
  US: 'United States',
  UY: 'Uruguay',
  UZ: 'Uzbekistan',
  VA: 'Vatican City',
  VC: 'St Vincent & the Grenadines',
  VE: 'Venezuela',
  VG: 'British Virgin Islands',
  VI: 'US Virgin Islands',
  VN: 'Vietnam',
  VU: 'Vanuatu',
  WF: 'Wallis & Futuna',
  WS: 'Samoa',
  YE: 'Yemen',
  YT: 'Mayotte',
  ZA: 'South Africa',
  ZM: 'Zambia',
  ZW: 'Zimbabwe',
};

/** Whether a code is an ISO 3166-1 alpha-2 country code (upper case). */
export function isCountryCode(code: string | null | undefined): boolean {
  return !!code && Object.prototype.hasOwnProperty.call(COUNTRY_NAMES, code);
}

/** The country select's options: the United Kingdom first, then every other country by name. */
export const COUNTRY_OPTIONS: ReadonlyArray<{ code: string; name: string }> = [
  { code: 'GB', name: COUNTRY_NAMES.GB },
  ...Object.entries(COUNTRY_NAMES)
    .filter(([code]) => code !== 'GB')
    .map(([code, name]) => ({ code, name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'en-GB')),
];

// The wording of each problem, shared with the lambdas' refusals
export const BILLING_PROBLEMS = {
  emailMissing: "Enter the company's email",
  emailShape: "Enter the company's email in the format name@example.com",
  emailLong: `The company's email must be ${MAX_EMAIL_LENGTH} characters or fewer`,
  phoneLong: `The phone number must be ${MAX_PHONE_LENGTH} characters or fewer`,
  address1Missing: 'Enter the first line of the address',
  address1Long: `The first line of the address must be ${MAX_FIELD_LENGTH} characters or fewer`,
  address2Long: `The second line of the address must be ${MAX_FIELD_LENGTH} characters or fewer`,
  cityMissing: 'Enter the town or city',
  cityLong: `The town or city must be ${MAX_FIELD_LENGTH} characters or fewer`,
  postcodeMissing: 'Enter the postcode',
  postcodeLong: `The postcode must be ${MAX_FIELD_LENGTH} characters or fewer`,
  country: 'Choose a country',
} as const;

const PROBLEM_FIELDS: Record<keyof typeof BILLING_PROBLEMS, BillingField> = {
  emailMissing: 'email',
  emailShape: 'email',
  emailLong: 'email',
  phoneLong: 'phone',
  address1Missing: 'address1',
  address1Long: 'address1',
  address2Long: 'address2',
  cityMissing: 'city',
  cityLong: 'city',
  postcodeMissing: 'postcode',
  postcodeLong: 'postcode',
  country: 'country',
};

const FORM_ORDER: BillingField[] = ['email', 'phone', 'address1', 'address2', 'city', 'postcode', 'country'];

// One @, no spaces, no dot at either end of the name or two together, and a domain of dot-separated parts ending in one of
// two or more characters: Stripe refuses a customer email such as name@acme.co.uk. or name@acme..com
const EMAIL_SHAPE = /^(?!\.)(?!.*\.\.)[^\s@]+(?<!\.)@(?:[^\s@.]+\.)+[^\s@.]{2,}$/;

type Text = string | null | undefined;

const trimmed = (value: Text) => (typeof value === 'string' ? value.trim() : '');
const orNull = (value: string) => (value === '' ? null : value);

/**
 * Billing details as sent and stored: every string trimmed, an empty phone or second address line null, the country upper
 * case. Missing required fields come back as '' (validateBillingDetails says which).
 */
export function normaliseBillingDetails(
  input:
    | {
        email?: Text;
        phone?: Text;
        address?: {
          address1?: Text;
          address2?: Text;
          city?: Text;
          postcode?: Text;
          country?: Text;
        } | null;
      }
    | null
    | undefined,
): BillingDetails {
  const address = input?.address ?? {};
  return {
    email: trimmed(input?.email),
    phone: orNull(trimmed(input?.phone)),
    address: {
      address1: trimmed(address.address1),
      address2: orNull(trimmed(address.address2)),
      city: trimmed(address.city),
      postcode: trimmed(address.postcode),
      country: trimmed(address.country).toUpperCase(),
    },
  };
}

/** The form's typed text as billing details (normalised). */
export function billingFromDraft(draft: BillingDraft): BillingDetails {
  const { email, phone, address1, address2, city, postcode, country } = draft;
  return normaliseBillingDetails({ email, phone, address: { address1, address2, city, postcode, country } });
}

/** Billing details as the form's text, '' for each missing value. */
export function draftFromBilling(billing: BillingDetails | null | undefined): BillingDraft {
  if (!billing) return { ...EMPTY_BILLING_DRAFT };
  const { address } = billing;
  return {
    email: billing.email ?? '',
    phone: billing.phone ?? '',
    address1: address?.address1 ?? '',
    address2: address?.address2 ?? '',
    city: address?.city ?? '',
    postcode: address?.postcode ?? '',
    country: address?.country ?? '',
  };
}

/** The problems with normalised billing details, at most one per field, in the form's order; none when they're fine. */
export function validateBillingDetails(billing: BillingDetails): BillingProblem[] {
  const problems: BillingProblem[] = [];
  const add = (key: keyof typeof BILLING_PROBLEMS) =>
    problems.push({ field: PROBLEM_FIELDS[key], reason: BILLING_PROBLEMS[key] });
  const { email, phone, address } = billing;

  if (!email) add('emailMissing');
  else if (email.length > MAX_EMAIL_LENGTH) add('emailLong');
  else if (!EMAIL_SHAPE.test(email)) add('emailShape');

  if (phone !== null && phone.length > MAX_PHONE_LENGTH) add('phoneLong');

  if (!address.address1) add('address1Missing');
  else if (address.address1.length > MAX_FIELD_LENGTH) add('address1Long');
  if (address.address2 !== null && address.address2.length > MAX_FIELD_LENGTH) add('address2Long');
  if (!address.city) add('cityMissing');
  else if (address.city.length > MAX_FIELD_LENGTH) add('cityLong');
  if (!address.postcode) add('postcodeMissing');
  else if (address.postcode.length > MAX_FIELD_LENGTH) add('postcodeLong');

  if (!isCountryCode(address.country)) add('country');
  return problems;
}

/** The form's text as billing details to send, or its problems. */
export function parseBillingDraft(
  draft: BillingDraft,
): { ok: true; billing: BillingDetails } | { ok: false; problems: BillingProblem[] } {
  const billing = billingFromDraft(draft);
  const problems = validateBillingDetails(billing);
  return problems.length > 0 ? { ok: false, problems } : { ok: true, billing };
}

/** The fields a server refusal's message names, by the shared wording; empty when it names none. */
export function billingProblemsFromMessage(message: string | null | undefined): BillingProblem[] {
  if (!message) return [];
  const found: Partial<Record<BillingField, string>> = {};
  for (const [key, reason] of Object.entries(BILLING_PROBLEMS) as [keyof typeof BILLING_PROBLEMS, string][]) {
    const field = PROBLEM_FIELDS[key];
    // The longest wording that matches wins ("…email in the format…" holds "…email")
    if (message.includes(reason) && reason.length > (found[field]?.length ?? 0)) found[field] = reason;
  }
  return FORM_ORDER.flatMap((field) => {
    const reason = found[field];
    return reason ? [{ field, reason }] : [];
  });
}

// The Companies House country names that are the United Kingdom
const UK_COUNTRY_NAMES = new Set([
  'england',
  'wales',
  'scotland',
  'northern ireland',
  'united kingdom',
  'great britain',
]);

/**
 * A registered office's country as an ISO-2 code: the UK's names (any case) are GB, a code is kept, and anything else is
 * '' so the payer chooses it.
 */
export function countryCodeFromName(country: Text): string {
  const value = trimmed(country);
  if (UK_COUNTRY_NAMES.has(value.toLowerCase())) return 'GB';
  const code = value.toUpperCase();
  return value.length === 2 && isCountryCode(code) ? code : '';
}

interface PrefillCompany {
  companiesRegistrar?: Array<{
    name?: string | null;
    registrarType?: string | null;
    registeredAddress?: {
      address1?: Text;
      address2?: Text;
      city?: Text;
      postcode?: Text;
      country?: Text;
    } | null;
  }> | null;
  profileInfo?: { contactEmail?: Text; contactPhone?: Text } | null;
}

/**
 * The billing form's starting text from the company: the address from its Companies House registered office (the county
 * dropped, the country as an ISO-2 code), the email from `profileInfo.contactEmail` and the phone from `contactPhone`.
 * Anything missing is left blank; nothing comes from the person paying.
 */
export function billingPrefillFromCompany(company: PrefillCompany | null | undefined): BillingDraft {
  const office = company?.companiesRegistrar?.find((entry) => isRegistrar(entry, 'COMPANIES_HOUSE'))?.registeredAddress;
  return {
    email: trimmed(company?.profileInfo?.contactEmail),
    phone: trimmed(company?.profileInfo?.contactPhone),
    address1: trimmed(office?.address1),
    address2: trimmed(office?.address2),
    city: trimmed(office?.city),
    postcode: trimmed(office?.postcode),
    country: countryCodeFromName(office?.country),
  };
}

/**
 * Stripe's Payment Element options that start its postcode and country at the billing details' ones, so the payer isn't
 * asked again. They stay editable: the card issuer checks them against the address the card is registered to, which can
 * differ from the invoice address. A blank postcode, or a country that isn't an ISO-2 code, is left for Stripe to ask.
 */
export function paymentElementAddressDefaults(address: { postcode?: Text; country?: Text } | null | undefined): {
  defaultValues?: { billingDetails: { address: { postal_code?: string; country?: string } } };
} {
  const postcode = trimmed(address?.postcode);
  const country = trimmed(address?.country).toUpperCase();
  const known = {
    ...(postcode ? { postal_code: postcode } : {}),
    ...(isCountryCode(country) ? { country } : {}),
  };
  return Object.keys(known).length ? { defaultValues: { billingDetails: { address: known } } } : {};
}
